/**
 * The player mat on the table: six carved trays round an oval, a Trajan slot
 * outside each, the Arch, the "+2" plates, and the middle of the ring. Draws a
 * `MatShow` and nothing else — what the mat means is worked out in
 * `mat/matShow.ts`, from the engine.
 */
import { Container, FillGradient, FillPattern, Graphics } from 'pixi.js';
import { CX, CY, MARKER, RING_RX, RING_RY } from '../../mat/geometry';
import type { MatShow } from '../../mat/matShow';
import { MAT, MAT_AT, markerRect, plus2Rect, slotRect, trayRect, type Rect } from '../layout';
import { parchmentTexture } from '../materials';
import { ACCENT, BRONZE, BRONZE_DARK, BRONZE_LIGHT, GOLD, INK, INK_SOFT, mix } from '../palette';
import type { Register } from './board';
import { icon } from './icons';
import { actionMarker, boxShadow, emptySpace, label } from './pieces';
import { trajanFace } from './tiles';

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI'];

const at = <T extends Container>(parent: Container, child: T, x: number, y: number): T => {
  child.position.set(x, y);
  parent.addChild(child);
  return child;
};

/** The mat's board and the faint oval the trays sit on. */
export function staticMat(): Container {
  const root = new Container();
  at(root, boxShadow(MAT.w, MAT.h, 8, 0.65, 10), MAT.x, MAT.y);
  const g = new Graphics();
  g.roundRect(MAT.x, MAT.y + 4, MAT.w, MAT.h, 22).fill(0x7a5a34);
  g.roundRect(MAT.x, MAT.y, MAT.w, MAT.h, 22).fill(new FillPattern(parchmentTexture(), 'repeat'));
  g.roundRect(MAT.x, MAT.y, MAT.w, MAT.h, 22).fill({ color: 0x9c7a4a, alpha: 0.22 });
  g.roundRect(MAT.x + 1.5, MAT.y + 1.5, MAT.w - 3, MAT.h - 3, 21).stroke({
    width: 2,
    color: 0xffffff,
    alpha: 0.35,
  });
  g.roundRect(MAT.x + 7, MAT.y + 7, MAT.w - 14, MAT.h - 14, 17).stroke({
    width: 1.2,
    color: BRONZE,
    alpha: 0.6,
  });
  root.addChild(g);

  const ring = new Graphics();
  const steps = 96;
  for (let k = 0; k < steps; k += 2) {
    const a0 = (k / steps) * Math.PI * 2;
    const a1 = ((k + 1) / steps) * Math.PI * 2;
    ring
      .moveTo(MAT_AT.x + CX + RING_RX * Math.sin(a0), MAT_AT.y + CY - RING_RY * Math.cos(a0))
      .lineTo(MAT_AT.x + CX + RING_RX * Math.sin(a1), MAT_AT.y + CY - RING_RY * Math.cos(a1));
  }
  ring.stroke({ width: 1.2, color: BRONZE_DARK, alpha: 0.35 });
  root.addChild(ring);

  return root;
}

function trayBody(r: Rect, state: MatShow['trays'][number]['state']): Graphics {
  const g = new Graphics();
  g.roundRect(r.x, r.y + 2.5, r.w, r.h, 12).fill(0x5a3d22);
  g.roundRect(r.x, r.y, r.w, r.h, 12).fill(
    new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: { x: 0.2, y: 1 },
      colorStops: [
        { offset: 0, color: 0xc49a66 },
        { offset: 1, color: 0x94693d },
      ],
      textureSpace: 'local',
    }),
  );
  // The hollow the markers lie in.
  g.roundRect(r.x + 5, r.y + 16, r.w - 10, r.h - 21, 8).fill(0x6b4724);
  g.roundRect(r.x + 5, r.y + 17.5, r.w - 10, r.h - 22.5, 8).fill(0x8a6038);
  g.roundRect(r.x, r.y, r.w, r.h, 12).stroke({
    width: state === 'target' || state === 'open' ? 2.4 : 1,
    color: state === 'target' || state === 'open' ? GOLD : 0x3a2412,
    alpha: state === 'target' || state === 'open' ? 1 : 0.8,
  });
  if (state === 'source') {
    g.roundRect(r.x, r.y, r.w, r.h, 12).fill({ color: 0x000000, alpha: 0.25 });
  }
  return g;
}

export function matPieces(show: MatShow, register: Register): Container {
  const root = new Container();

  show.trays.forEach((t, i) => {
    const r = trayRect(i);
    const tray = new Container();
    root.addChild(tray);
    register(`tray:${i}`, tray);
    tray.addChild(trayBody(r, t.state));
    if (t.state === 'dim') tray.alpha = 0.62;
    at(tray, icon(t.action, 11, 0x3a2412), r.x + 11, r.y + 9);
    at(
      tray,
      label(t.action.charAt(0).toUpperCase() + t.action.slice(1), 11, {
        fill: 0x2b1a0c,
        weight: '700',
        anchor: [0, 0.5],
      }),
      r.x + 19,
      r.y + 9,
    );

    const count = t.resting.length + t.moving.length;
    t.resting.forEach((color, k) => {
      const m = markerRect(i, k, t.resting.length, t.restingRow);
      register.piece(
        `marker-${i}-${k}`,
        at(tray, actionMarker(color, MARKER), m.x + m.w / 2, m.y + m.h / 2),
      );
    });
    t.moving.forEach((mk, k) => {
      if (mk.dragging) return;
      const m = markerRect(i, k, t.moving.length, t.movingRow);
      const c = at(tray, new Container(), m.x + m.w / 2, m.y + m.h / 2);
      const halo = new Graphics()
        .circle(0, 0, MARKER * 0.8)
        .fill({ color: 0xfff3c4, alpha: mk.held ? 0.95 : 0.35 });
      if (mk.last) halo.circle(0, 0, MARKER * 0.8).stroke({ width: 1.6, color: ACCENT });
      c.addChild(halo);
      const piece = at(c, actionMarker(mk.color, MARKER), 0, mk.held ? -3 : 0);
      piece.scale.set(mk.held ? 1.12 : 1);
      register(`marker:${mk.index}`, c);
    });
    if (count === 0) {
      at(tray, label(t.empty, 10, { fill: 0xe8d8bc, weight: '500' }), r.x + r.w / 2, r.y + 30);
    }

    if (t.forecast) {
      const f = t.forecast;
      const pill = new Graphics();
      const w = f.completes ? 58 : 44;
      const px = r.x + r.w / 2 - w / 2;
      const py = r.y + r.h - 18;
      pill
        .roundRect(px, py, w, 14, 7)
        .fill(f.completes ? 0xf6dc86 : 0xf2e6cc)
        .stroke({ width: 1, color: 0x3a2412, alpha: 0.6 });
      tray.addChild(pill);
      at(tray, label('→', 10, { fill: INK_SOFT, weight: '700' }), px + 8, py + 7);
      at(tray, icon(f.target, 10, INK), px + 19, py + 7);
      at(tray, label(String(f.moved), 10.5, { face: 'display', fill: INK }), px + 31, py + 7.5);
      if (f.completes) at(tray, icon('star', 10, ACCENT), px + 45, py + 7);
    }
    const p = plus2Rect(i);
    const plate = new Graphics().roundRect(p.x, p.y, p.w, p.h, 4);
    if (t.plus2) plate.fill(ACCENT).stroke({ width: 1, color: 0x4a1410 });
    else plate.fill(0xe9dcc0).stroke({ width: 1, color: 0x6b4a2a, alpha: 0.8 });
    tray.addChild(plate);
    at(
      tray,
      label('+2', 9.5, { face: 'display', fill: t.plus2 ? 0xfff1e0 : 0xb8a07a }),
      p.x + p.w / 2,
      p.y + p.h / 2 + 0.5,
    );
  });

  show.slots.forEach((s, i) => {
    const r = slotRect(i);
    const slot = new Container();
    root.addChild(slot);
    register(`slot:${i}`, slot);
    if (s.tile) {
      register.piece(s.tile.id, at(slot, trajanFace(s.tile, r.w), r.x, r.y));
    } else {
      at(slot, emptySpace(r.w, r.h, 7), r.x, r.y);
      if (s.tone === 'free') {
        at(
          slot,
          label('put a\ntile here', 9.5, { fill: INK_SOFT, weight: '500' }),
          r.x + r.w / 2,
          r.y + r.h / 2 + 4,
        );
      }
    }
    const badge = new Graphics()
      .circle(r.x + 3, r.y + 3, 9)
      .fill(BRONZE_DARK)
      .stroke({ width: 1, color: BRONZE_LIGHT });
    slot.addChild(badge);
    at(
      slot,
      label(ROMAN[i]!, ROMAN[i]!.length > 2 ? 7 : 8.5, { face: 'display', fill: 0xfff1d6 }),
      r.x + 3,
      r.y + 3.5,
    );
    if (s.tone === 'completes' || s.target) {
      const ring = new Graphics().roundRect(r.x - 3, r.y - 3, r.w + 6, r.h + 6, 9);
      ring.stroke({
        width: s.tone === 'completes' ? 3 : 2,
        color: s.tone === 'completes' ? GOLD : ACCENT,
        alpha: 0.95,
      });
      slot.addChild(ring);
    }
    if (s.tone === 'short') {
      const ring = new Graphics();
      const pad = 3;
      const x0 = r.x - pad;
      const y0 = r.y - pad;
      const w = r.w + 2 * pad;
      const h = r.h + 2 * pad;
      for (let d = 0; d < w; d += 8)
        ring
          .moveTo(x0 + d, y0)
          .lineTo(x0 + Math.min(w, d + 4), y0)
          .moveTo(x0 + d, y0 + h)
          .lineTo(x0 + Math.min(w, d + 4), y0 + h);
      for (let d = 0; d < h; d += 8)
        ring
          .moveTo(x0, y0 + d)
          .lineTo(x0, y0 + Math.min(h, d + 4))
          .moveTo(x0 + w, y0 + d)
          .lineTo(x0 + w, y0 + Math.min(h, d + 4));
      ring.stroke({ width: 1.8, color: ACCENT, alpha: 0.9 });
      slot.addChild(ring);
    }
    if (s.tone === 'completes' || s.match) {
      const star = new Graphics()
        .circle(r.x + r.w, r.y, 9)
        .fill(ACCENT)
        .stroke({ width: 1, color: 0xfff1e0 });
      slot.addChild(star);
      at(slot, icon('star', 11, 0xfff1d0), r.x + r.w, r.y);
    }
    if (s.arch) {
      const arch = new Container();
      const plate = new Graphics().circle(0, 0, 11).fill(
        new FillGradient({
          type: 'radial',
          center: { x: 0.35, y: 0.3 },
          innerRadius: 0,
          outerCenter: { x: 0.5, y: 0.5 },
          outerRadius: 0.6,
          colorStops: [
            { offset: 0, color: BRONZE_LIGHT },
            { offset: 1, color: BRONZE_DARK },
          ],
          textureSpace: 'local',
        }),
      );
      arch.addChild(plate);
      at(arch, icon('arch', 14, 0xfff4dc), 0, 0);
      at(
        slot,
        arch,
        s.tile ? r.x + r.w - 2 : r.x + r.w / 2,
        s.tile ? r.y + r.h - 2 : r.y + r.h / 2,
      );
    }
  });

  // The route last, over the trays it passes through, so the whole move can be followed.
  if (show.route) root.addChild(route(show.route.source, show.route.steps));
  root.addChild(centre(show));
  return root;
}

function centre(show: MatShow): Container {
  const c = new Container();
  const x = MAT_AT.x + CX;
  const y = MAT_AT.y + CY;
  const k = show.centre;
  switch (k.kind) {
    case 'standing': {
      if (k.archHome) at(c, icon('arch', 18, BRONZE_DARK), x, y - 24);
      at(c, label(String(k.vp), 28, { face: 'display', fill: INK }), x, y - 2);
      at(
        c,
        label('VICTORY POINTS', 8.5, { face: 'display', fill: BRONZE_DARK, spacing: 1 }),
        x,
        y + 18,
      );
      break;
    }
    case 'placing': {
      at(c, icon(k.action, 18, INK), x, y - 20);
      at(c, label(k.action, 13, { face: 'display', fill: INK }), x, y - 2);
      at(c, label(`time ${k.moved}`, 11, { fill: INK_SOFT }), x, y + 13);
      at(
        c,
        label(k.completes ? `★ completes ${ROMAN[k.slot]}` : 'no tile completed', 10.5, {
          fill: k.completes ? ACCENT : INK_SOFT,
          weight: '700',
        }),
        x,
        y + 27,
      );
      break;
    }
    case 'arranging': {
      at(
        c,
        label('YOUR OPENING', 10, { face: 'display', fill: BRONZE_DARK, spacing: 1 }),
        x,
        y - 18,
      );
      at(c, label('two per tray,\ntwo of each colour', 10.5, { fill: INK_SOFT }), x, y + 6);
      break;
    }
    case 'tiles': {
      at(
        c,
        label('TRAJAN TILES', 10, { face: 'display', fill: BRONZE_DARK, spacing: 1 }),
        x,
        y - 20,
      );
      at(c, label(`${k.placed} of ${k.of} placed`, 13, { face: 'display', fill: INK }), x, y - 2);
      at(
        c,
        label(k.holding ? 'now choose a slot' : 'choose a stack', 10.5, { fill: INK_SOFT }),
        x,
        y + 16,
      );
      break;
    }
  }
  return c;
}

/** The clockwise route a tray's markers take, as dashes along the oval. */
function route(source: number, steps: number): Graphics {
  const g = new Graphics();
  const from = (source / 6) * Math.PI * 2;
  const to = ((source + steps) / 6) * Math.PI * 2;
  const n = Math.max(12, Math.round(steps * 14));
  for (let k = 0; k < n; k += 2) {
    const a0 = from + ((to - from) * k) / n;
    const a1 = from + ((to - from) * (k + 1)) / n;
    g.moveTo(MAT_AT.x + CX + RING_RX * Math.sin(a0), MAT_AT.y + CY - RING_RY * Math.cos(a0)).lineTo(
      MAT_AT.x + CX + RING_RX * Math.sin(a1),
      MAT_AT.y + CY - RING_RY * Math.cos(a1),
    );
  }
  g.stroke({ width: 3.2, color: mix(ACCENT, GOLD, 0.25), alpha: 0.9, cap: 'round' });
  // A bead on every tray a marker drops into, the last one larger: it decides the action.
  for (let k = 1; k <= steps; k++) {
    const a = ((source + k) / 6) * Math.PI * 2;
    const x = MAT_AT.x + CX + RING_RX * Math.sin(a);
    const y = MAT_AT.y + CY - RING_RY * Math.cos(a);
    g.circle(x, y, k === steps ? 6.5 : 4).fill(k === steps ? ACCENT : mix(ACCENT, GOLD, 0.4));
    g.circle(x, y, k === steps ? 6.5 : 4).stroke({ width: 1.2, color: 0xfff1e0 });
  }
  return g;
}
